import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { schoolConfigApi, schoolYearsApi, feeConfigApi, classFeeConfigApi, fiscalApi } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Gear, Calendar, Money, Plus, Check, Trash, FloppyDisk, Archive, Lock, GraduationCap, ChalkboardTeacher, CreditCard, Cake, Fingerprint, WarningCircle, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import BulletinsConfigTab from '../components/settings/BulletinsConfigTab';
import SectionsAndTeachersTab from '../components/settings/SectionsAndTeachersTab';
import PaymentSettingsTab from '../components/settings/PaymentSettingsTab';
import SchoolCalendarTab from '../components/settings/SchoolCalendarTab';
import BirthdaySmsTab from '../components/settings/BirthdaySmsTab';
import SmsTemplatesTab from '../components/settings/SmsTemplatesTab';
import SmsHistoryTable from '../components/settings/SmsHistoryTable';
import UnpaidAutoRulesCard from '../components/settings/UnpaidAutoRulesCard';
import HikvisionTab from '../components/settings/HikvisionTab';
import { compressImage, fmtBytes } from '../lib/imageCompress';

const SchoolSettings = () => {
  const { t, language } = useLanguage();
  const [config, setConfig] = useState({ name: '', founder: '', director_studies: '', address: '', phone: '', email: '', logo_url: '' });
  const [years, setYears] = useState([]);
  const [activeYear, setActiveYear] = useState('');
  const [feeConfig, setFeeConfig] = useState({ registration_fee: 0, reregistration_fee: 0, transport_zones: [] });
  const [classFees, setClassFees] = useState([]);
  const [feesCoverage, setFeesCoverage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Year dialogs
  const [yearDialogOpen, setYearDialogOpen] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [yearForm, setYearForm] = useState({ name: '', start_date: '', end_date: '', is_active: false });

  // Zone dialog
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [zoneForm, setZoneForm] = useState({ name: '', amount: 0 });

  // Year closure dialog
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closingYear, setClosingYear] = useState(null);
  const [closingBusy, setClosingBusy] = useState(false);
  const [archives, setArchives] = useState([]);

  const loadArchives = async () => {
    try {
      const { data } = await fiscalApi.listArchives();
      setArchives(data);
    } catch { /* silent */ }
  };

  const openCloseDialog = (y) => {
    setClosingYear(y);
    setCloseDialogOpen(true);
  };

  const confirmCloseYear = async () => {
    if (!closingYear) return;
    setClosingBusy(true);
    try {
      const { data } = await fiscalApi.closeYear(closingYear.name);
      toast.success(`${closingYear.name} clôturée — ${Math.round(data.summary.total_payments).toLocaleString('fr-FR')} FCFA encaissés archivés`);
      setCloseDialogOpen(false);
      setClosingYear(null);
      // Refresh data but keep user on the same tab
      const [yearsRes, archivesRes] = await Promise.all([
        schoolYearsApi.getAll(),
        fiscalApi.listArchives().catch(() => ({ data: [] })),
      ]);
      setYears(yearsRes.data);
      setArchives(archivesRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la clôture');
    } finally {
      setClosingBusy(false);
    }
  };

  useEffect(() => {
    loadAll();
    loadArchives();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [cfgRes, yearsRes, activeRes] = await Promise.all([
        schoolConfigApi.get(),
        schoolYearsApi.getAll(),
        schoolYearsApi.getActive()
      ]);
      setConfig({ ...config, ...cfgRes.data });
      setYears(yearsRes.data);
      const activeName = activeRes.data?.name || '';
      setActiveYear(activeName);
      if (activeName) {
        const [feeRes, classRes] = await Promise.all([
          feeConfigApi.get(activeName),
          classFeeConfigApi.getAll(activeName)
        ]);
        setFeeConfig({
          registration_fee: feeRes.data.registration_fee || 0,
          reregistration_fee: feeRes.data.reregistration_fee || 0,
          transport_zones: feeRes.data.transport_zones || []
        });
        setClassFees(classRes.data || []);
        api.get('/class-fees/coverage', { params: { school_year: activeName } })
          .then(r => setFeesCoverage(r.data)).catch(() => setFeesCoverage(null));
      }
    } catch (e) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      await schoolConfigApi.update(config);
      toast.success(language === 'fr' ? 'Paramètres enregistrés' : 'Settings saved');
    } catch {
      toast.error(t('error'));
    }
  };

  const openNewYearDialog = () => {
    setEditingYear(null);
    setYearForm({ name: '', start_date: '', end_date: '', is_active: false });
    setYearDialogOpen(true);
  };

  const openEditYearDialog = (y) => {
    setEditingYear(y);
    setYearForm({ name: y.name, start_date: y.start_date, end_date: y.end_date, is_active: !!y.is_active });
    setYearDialogOpen(true);
  };

  const handleSaveYear = async (e) => {
    e.preventDefault();
    try {
      if (editingYear) {
        await schoolYearsApi.update(editingYear.id, yearForm);
      } else {
        await schoolYearsApi.create(yearForm);
      }
      toast.success(t('success'));
      await loadAll();
      setYearDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteYear = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await schoolYearsApi.delete(id);
      toast.success(t('success'));
      await loadAll();
    } catch {
      toast.error(t('error'));
    }
  };

  const handleSaveFees = async () => {
    try {
      await feeConfigApi.update({
        school_year: activeYear,
        registration_fee: Number(feeConfig.registration_fee) || 0,
        reregistration_fee: Number(feeConfig.reregistration_fee) || 0,
        transport_zones: feeConfig.transport_zones
      });
      toast.success(language === 'fr' ? 'Frais enregistrés' : 'Fees saved');
      await loadAll();
    } catch {
      toast.error(t('error'));
    }
  };

  const handleAddZone = async (e) => {
    e.preventDefault();
    try {
      await feeConfigApi.addTransportZone({
        school_year: activeYear,
        name: zoneForm.name,
        amount: Number(zoneForm.amount) || 0
      });
      toast.success(t('success'));
      setZoneForm({ name: '', amount: 0 });
      await loadAll();
      setZoneDialogOpen(false);
    } catch {
      toast.error(t('error'));
    }
  };

  const handleZoneChange = (idx, field, value) => {
    const next = [...feeConfig.transport_zones];
    next[idx] = { ...next[idx], [field]: field === 'amount' ? Number(value) || 0 : value };
    setFeeConfig({ ...feeConfig, transport_zones: next });
  };

  const handleRemoveZone = (idx) => {
    const next = [...feeConfig.transport_zones];
    next.splice(idx, 1);
    setFeeConfig({ ...feeConfig, transport_zones: next });
  };

  const handleClassFeeChange = (idx, field, value) => {
    const next = [...classFees];
    next[idx] = { ...next[idx], [field]: Number(value) || 0 };
    setClassFees(next);
  };

  const handleSaveClassFee = async (cf) => {
    try {
      await classFeeConfigApi.update(cf.class_name, {
        class_name: cf.class_name,
        school_year: activeYear,
        tuition_amount: Number(cf.tuition_amount) || 0,
        canteen_amount: Number(cf.canteen_amount) || 0
      });
      toast.success(language === 'fr' ? `Frais ${cf.class_name} enregistrés` : `${cf.class_name} fees saved`);
    } catch {
      toast.error(t('error'));
    }
  };

  const formatCurrency = (a) => new Intl.NumberFormat('fr-FR').format(a || 0) + ' FCFA';

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="school-settings-page">
      <div className="flex items-center gap-3">
        <Gear size={28} className="text-[#0052FF]" weight="duotone" />
        <h1 className="font-outfit text-3xl font-bold text-[#0F172A]">{t('schoolSettings')}</h1>
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get('tab') || 'general'} className="space-y-6">
        <TabsList className="bg-[#F1F5F9]">
          <TabsTrigger value="general" data-testid="tab-general">{t('generalInfo')}</TabsTrigger>
          <TabsTrigger value="years" data-testid="tab-years">{t('schoolYears')}</TabsTrigger>
          <TabsTrigger value="fees" data-testid="tab-fees">{t('feeConfig')}</TabsTrigger>
          <TabsTrigger value="class-fees" data-testid="tab-class-fees">{t('classFeeConfig')}</TabsTrigger>
          <TabsTrigger value="bulletins" data-testid="tab-bulletins-config">
            <GraduationCap size={16} className="mr-1.5" />
            Bulletins & Notes
          </TabsTrigger>
          <TabsTrigger value="sections-teachers" data-testid="tab-sections-teachers">
            <ChalkboardTeacher size={16} className="mr-1.5" />
            Sections & Enseignants
          </TabsTrigger>
          <TabsTrigger value="payments-online" data-testid="tab-payments-online">
            <CreditCard size={16} className="mr-1.5" />
            Paiements en ligne
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">
            <Calendar size={16} className="mr-1.5" />
            Calendrier
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Cake size={16} className="mr-1.5" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="pointeuse" data-testid="tab-pointeuse">
            <Fingerprint size={16} className="mr-1.5" />
            Pointeuse
          </TabsTrigger>
        </TabsList>

        {/* General */}
        <TabsContent value="general">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader><CardTitle className="font-outfit">{t('generalInfo')}</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSaveConfig} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('schoolName')}</Label>
                  <Input data-testid="config-name" value={config.name || ''} onChange={(e) => setConfig({ ...config, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>{t('founder')}</Label>
                  <Input data-testid="config-founder" value={config.founder || ''} onChange={(e) => setConfig({ ...config, founder: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('directorOfStudies')}</Label>
                  <Input data-testid="config-director" value={config.director_studies || ''} onChange={(e) => setConfig({ ...config, director_studies: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('phone')}</Label>
                  <Input data-testid="config-phone" value={config.phone || ''} onChange={(e) => setConfig({ ...config, phone: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('email')}</Label>
                  <Input data-testid="config-email" type="email" value={config.email || ''} onChange={(e) => setConfig({ ...config, email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('address')}</Label>
                  <Input data-testid="config-address" value={config.address || ''} onChange={(e) => setConfig({ ...config, address: e.target.value })} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Logo de l&apos;école</Label>
                  <div className="flex items-start gap-4">
                    {config.logo_url ? (
                      <img
                        src={config.logo_url}
                        alt="Logo"
                        className="w-24 h-24 object-contain border border-[#E2E8F0] rounded-md bg-white p-1"
                        data-testid="config-logo-preview"
                      />
                    ) : (
                      <div className="w-24 h-24 border border-dashed border-[#CBD5E1] rounded-md flex items-center justify-center text-[10px] text-[#94A3B8] text-center px-1">
                        Aucun logo
                      </div>
                    )}
                    <div className="flex-1 space-y-2">
                      <Input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        data-testid="config-logo-file"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('Fichier trop volumineux (max 5 Mo avant compression).');
                            e.target.value = '';
                            return;
                          }
                          try {
                            const result = await compressImage(file);
                            setConfig({ ...config, logo_url: result.dataUrl });
                            if (result.skipped) {
                              toast.success('Logo prêt — pensez à Enregistrer.');
                            } else {
                              toast.success(
                                `Logo compressé : ${fmtBytes(result.originalSize)} → ${fmtBytes(result.compressedSize)} `
                                + `(${result.width}×${result.height} px). Pensez à Enregistrer.`,
                              );
                            }
                          } catch {
                            toast.error('Lecture ou compression du fichier impossible.');
                          }
                        }}
                      />
                      <p className="text-xs text-[#64748B]">
                        Formats : PNG, JPG, WEBP, SVG. Max 5 Mo — compression automatique à 512×512 px, qualité 85%. Le logo est stocké en base directement.
                      </p>
                      {config.logo_url && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setConfig({ ...config, logo_url: '' })}
                          data-testid="config-logo-clear"
                          className="text-red-700 border-red-200 hover:bg-red-50"
                        >
                          <Trash size={12} className="mr-1" /> Retirer le logo
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Button type="submit" data-testid="save-config-btn" className="bg-[#0052FF] hover:bg-[#0040D9]">
                    <FloppyDisk size={18} className="mr-2" />{t('saveSettings')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* School Years */}
        <TabsContent value="years">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit flex items-center gap-2"><Calendar size={20} />{t('schoolYears')}</CardTitle>
              <Button onClick={openNewYearDialog} data-testid="add-year-btn" className="bg-[#0052FF] hover:bg-[#0040D9]">
                <Plus size={18} className="mr-2" />{t('addSchoolYear')}
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('schoolYear')}</TableHead>
                    <TableHead>{t('startDate')}</TableHead>
                    <TableHead>{t('endDate')}</TableHead>
                    <TableHead>{t('active')}</TableHead>
                    <TableHead className="text-right">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {years.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell></TableRow>
                  ) : (
                    years.map((y) => (
                      <TableRow key={y.id} data-testid={`year-row-${y.name}`}>
                        <TableCell className="font-medium">
                          {y.name}
                          {y.is_closed && (
                            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                              <Lock size={10} /> {t('closedYear') || 'Clôturée'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{y.start_date}</TableCell>
                        <TableCell>{y.end_date}</TableCell>
                        <TableCell>
                          {y.is_active ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700"><Check size={12} />{t('active')}</span>
                          ) : <span className="text-[#64748B] text-xs">—</span>}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          {!y.is_closed && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => openCloseDialog(y)}
                              data-testid={`close-year-${y.name}`}
                            >
                              <Lock size={14} className="mr-1" />{t('closeYear') || 'Clôturer'}
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => openEditYearDialog(y)} data-testid={`edit-year-${y.name}`}>{t('edit')}</Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteYear(y.id)} data-testid={`delete-year-${y.name}`}><Trash size={16} /></Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {archives.length > 0 && (
            <Card className="border border-[#E2E8F0] shadow-none mt-6" data-testid="archives-card">
              <CardHeader>
                <CardTitle className="font-outfit flex items-center gap-2">
                  <Archive size={20} className="text-amber-600" weight="duotone" />
                  {t('fiscalArchives') || 'Archives comptables'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('schoolYear')}</TableHead>
                      <TableHead className="text-right">Paiements</TableHead>
                      <TableHead className="text-right">Dépenses</TableHead>
                      <TableHead className="text-right">Salaires</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                      <TableHead>Clôturée le</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archives.map((a) => {
                      const s = a.summary || {};
                      const fmt = (n) => `${Math.round(n || 0).toLocaleString('fr-FR')} FCFA`;
                      return (
                        <TableRow key={a.school_year} data-testid={`archive-row-${a.school_year}`}>
                          <TableCell className="font-medium">{a.school_year}</TableCell>
                          <TableCell className="text-right text-emerald-700">{fmt(s.total_payments)}</TableCell>
                          <TableCell className="text-right text-red-700">{fmt(s.total_expenses)}</TableCell>
                          <TableCell className="text-right text-red-700">{fmt(s.total_salaries)}</TableCell>
                          <TableCell className={`text-right font-semibold ${(s.net_cash_flow || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(s.net_cash_flow)}</TableCell>
                          <TableCell className="text-xs text-[#64748B]">{(a.closed_at || '').slice(0, 10)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="fees" className="space-y-6">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader><CardTitle className="font-outfit flex items-center gap-2"><Money size={20} />{t('feeConfig')} — {activeYear}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('registrationFee')} (FCFA)</Label>
                  <Input type="number" data-testid="registration-fee" value={feeConfig.registration_fee} onChange={(e) => setFeeConfig({ ...feeConfig, registration_fee: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('reregistrationFee')} (FCFA)</Label>
                  <Input type="number" data-testid="reregistration-fee" value={feeConfig.reregistration_fee} onChange={(e) => setFeeConfig({ ...feeConfig, reregistration_fee: e.target.value })} />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-outfit text-base font-semibold">{t('transportZones')}</Label>
                  <Button size="sm" variant="outline" onClick={() => setZoneDialogOpen(true)} data-testid="add-zone-btn">
                    <Plus size={16} className="mr-1" />{t('addZone')}
                  </Button>
                </div>
                <div className="space-y-2">
                  {feeConfig.transport_zones.length === 0 && (
                    <p className="text-sm text-[#64748B]">{t('noData')}</p>
                  )}
                  {feeConfig.transport_zones.map((z, idx) => (
                    <div key={z.key || idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`zone-row-${idx}`}>
                      <Input className="col-span-5" value={z.name || ''} onChange={(e) => handleZoneChange(idx, 'name', e.target.value)} placeholder={t('zoneName')} />
                      <Input className="col-span-5" type="number" value={z.amount || 0} onChange={(e) => handleZoneChange(idx, 'amount', e.target.value)} placeholder={t('zoneAmount')} />
                      <Button className="col-span-2" size="sm" variant="ghost" onClick={() => handleRemoveZone(idx)}><Trash size={16} className="text-red-600" /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={handleSaveFees} data-testid="save-fees-btn" className="bg-[#0052FF] hover:bg-[#0040D9]">
                <FloppyDisk size={18} className="mr-2" />{t('saveSettings')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Class fees */}
        <TabsContent value="class-fees">
          {feesCoverage && (
            feesCoverage.ok ? (
              <div className="mb-4 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="fees-coverage-ok">
                <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0" />
                Toutes les {feesCoverage.total} classes ont des frais résolus. Les rapports sont fiables.
              </div>
            ) : (
              <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="fees-coverage-alert">
                <div className="flex items-center gap-2 font-medium">
                  <WarningCircle size={18} weight="fill" className="text-amber-600 shrink-0" />
                  {feesCoverage.missing.length} classe(s) sans frais résolus — à définir avant de vous fier aux rapports
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {feesCoverage.missing.map(m => (
                    <span key={m.name} className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2.5 py-0.5 text-xs" data-testid={`fees-missing-${m.name}`}>
                      {m.name}
                      {m.student_count > 0
                        ? <b className="text-amber-700">· {m.student_count} élève{m.student_count > 1 ? 's' : ''}</b>
                        : <span className="text-[#94A3B8]">· 0 élève</span>}
                    </span>
                  ))}
                </div>
                <div className="mt-2 text-xs text-amber-700">
                  Astuce : définissez les frais du niveau (ex. « CP ») puis cliquez « Cloner » pour les propager à ses sections (CP A, CP B…).
                </div>
              </div>
            )
          )}
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader><CardTitle className="font-outfit">{t('classFeeConfig')} — {activeYear}</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('class')}</TableHead>
                    <TableHead>{t('cycle')}</TableHead>
                    <TableHead>{t('tuition')} (FCFA)</TableHead>
                    <TableHead>{t('canteen')} (FCFA)</TableHead>
                    <TableHead className="text-right">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {classFees.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell></TableRow>
                  ) : (
                    classFees.map((cf, idx) => (
                      <TableRow key={cf.id || cf.class_name} data-testid={`class-fee-${cf.class_name}`}>
                        <TableCell className="font-medium">{cf.class_name}</TableCell>
                        <TableCell><span className="text-xs text-[#64748B]">{t(cf.cycle)}</span></TableCell>
                        <TableCell>
                          <Input type="number" value={cf.tuition_amount || 0} onChange={(e) => handleClassFeeChange(idx, 'tuition_amount', e.target.value)} className="w-32" />
                        </TableCell>
                        <TableCell>
                          <Input type="number" value={cf.canteen_amount || 0} onChange={(e) => handleClassFeeChange(idx, 'canteen_amount', e.target.value)} className="w-32" />
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" onClick={() => handleSaveClassFee(cf)} data-testid={`save-class-fee-${cf.class_name}`} className="bg-[#0052FF] hover:bg-[#0040D9]">
                            <FloppyDisk size={14} className="mr-1" />{t('save')}
                          </Button>
                          {/* iter95 — Clone parent-level fees to all sub-classes. */}
                          <Button size="sm" variant="outline"
                            data-testid={`clone-class-fee-${cf.class_name}`}
                            title="Cloner ces frais vers toutes les sous-classes (CM2 → CM2 A, CM2 B, …)"
                            onClick={async () => {
                              // eslint-disable-next-line no-alert
                              const overwrite = window.confirm(
                                `Cloner les frais de "${cf.class_name}" vers ses sous-classes ?\n\n` +
                                `OK = Écraser aussi les sous-classes qui ont déjà des frais\n` +
                                `Annuler = Ne pas cloner`);
                              if (!overwrite) return;
                              try {
                                const { data } = await api.post(
                                  `/class-fees/${encodeURIComponent(cf.class_name)}/clone-to-siblings?overwrite=1`);
                                const parts = [];
                                if (data.created?.length) parts.push(`créé: ${data.created.join(', ')}`);
                                if (data.updated?.length) parts.push(`mis à jour: ${data.updated.join(', ')}`);
                                if (!data.created?.length && !data.updated?.length)
                                  parts.push(data.reason || 'aucune modification');
                                toast.success(`Cloné : ${parts.join(' · ')}`);
                                await loadAll();
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Erreur');
                              }
                            }}
                          >
                            📋 Cloner
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bulletins" className="space-y-6">
          <BulletinsConfigTab activeSchoolYear={activeYear} />
        </TabsContent>

        <TabsContent value="sections-teachers" className="space-y-6">
          <SectionsAndTeachersTab schoolYear={activeYear} />
        </TabsContent>

        <TabsContent value="payments-online" className="space-y-6">
          <PaymentSettingsTab />
        </TabsContent>

        <TabsContent value="calendar" className="space-y-6">
          <SchoolCalendarTab schoolYears={years} />
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <BirthdaySmsTab />
          <SmsTemplatesTab />
          <UnpaidAutoRulesCard />
          <SmsHistoryTable />
        </TabsContent>

        <TabsContent value="pointeuse">
          <HikvisionTab />
        </TabsContent>
      </Tabs>

      {/* Year dialog */}
      <Dialog open={yearDialogOpen} onOpenChange={setYearDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-outfit">{editingYear ? t('edit') : t('addSchoolYear')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveYear} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('schoolYear')}</Label>
              <Input data-testid="year-name" placeholder="2025-2026" value={yearForm.name} onChange={(e) => setYearForm({ ...yearForm, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('startDate')}</Label>
                <Input data-testid="year-start" type="date" value={yearForm.start_date} onChange={(e) => setYearForm({ ...yearForm, start_date: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>{t('endDate')}</Label>
                <Input data-testid="year-end" type="date" value={yearForm.end_date} onChange={(e) => setYearForm({ ...yearForm, end_date: e.target.value })} required />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="year-active" checked={yearForm.is_active} onCheckedChange={(v) => setYearForm({ ...yearForm, is_active: v })} />
              <Label>{t('setActive')}</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setYearDialogOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" data-testid="save-year-btn" className="bg-[#0052FF] hover:bg-[#0040D9]">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Year closure dialog */}
      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent data-testid="close-year-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Lock size={20} className="text-amber-600" weight="duotone" />
              Clôturer {closingYear?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 font-manrope text-sm">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-900">
              ⚠ Cette action est <strong>irréversible</strong> côté comptabilité. Toutes les données financières de l&apos;année (paiements, dépenses, salaires, frais, configurations) seront archivées dans <code>db.fiscal_archives</code> et l&apos;année sera marquée comme clôturée.
            </div>
            <ul className="list-disc list-inside text-[#0F172A] space-y-1">
              <li>Aucune donnée n&apos;est supprimée — les paiements restent visibles.</li>
              <li>L&apos;année active actuelle deviendra <strong>inactive</strong> ; activez ensuite la nouvelle année manuellement.</li>
              <li>L&apos;archive sera consultable depuis cette page et via <code>/api/fiscal/archives/{closingYear?.name}</code>.</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialogOpen(false)}>{t('cancel')}</Button>
            <Button
              onClick={confirmCloseYear}
              disabled={closingBusy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-close-year-btn"
            >
              {closingBusy ? 'Clôture en cours…' : `Clôturer définitivement ${closingYear?.name}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zone dialog */}
      <Dialog open={zoneDialogOpen} onOpenChange={setZoneDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-outfit">{t('addZone')}</DialogTitle></DialogHeader>
          <form onSubmit={handleAddZone} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('zoneName')}</Label>
              <Input data-testid="new-zone-name" value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} required placeholder="Zone 4" />
            </div>
            <div className="space-y-2">
              <Label>{t('zoneAmount')} (FCFA)</Label>
              <Input data-testid="new-zone-amount" type="number" value={zoneForm.amount} onChange={(e) => setZoneForm({ ...zoneForm, amount: e.target.value })} required />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setZoneDialogOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" data-testid="save-zone-btn" className="bg-[#0052FF] hover:bg-[#0040D9]">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchoolSettings;
